import { type FC, useId } from "react";

import { clsx } from "clsx";

// Single-select segmented control (slate track, white active segment) — the
// reusable "pick one" control used by the inbox status filter and the composer
// scope picker. Matches the mockup's .seg / .seg-item.

export type SegmentedDot = "amber" | "slate" | "indigo" | "emerald";

const DOT: Record<SegmentedDot, string> = {
  amber: "bg-amber-500",
  slate: "bg-slate-400",
  indigo: "bg-indigo-500",
  emerald: "bg-emerald-500",
};

export type SegmentedItem = {
  value: string;
  label: string;
  // Trailing count; omit to render a label-only segment.
  count?: number;
  // Optional leading status dot (status filter); omit for plain segments.
  dot?: SegmentedDot;
  disabled?: boolean;
  disabledHint?: string;
};

// `pulse` rings the control once to draw the eye.
export const Segmented: FC<{
  items: SegmentedItem[];
  selectedValue: string;
  pulse?: boolean;
  onChange(value: string): void;
}> = ({ items, selectedValue, pulse = false, onChange }) => {
  const tipId = useId();

  return (
    <div
      className={clsx(
        "inline-flex max-w-full min-w-0 items-center gap-0.5 overflow-x-auto rounded-full bg-[rgba(18,53,91,.07)] p-[3px]",
        pulse && "summary-pulse",
      )}
    >
      {items.map((item) => {
        const active = item.value === selectedValue;
        // A disabled tab says why it's disabled.
        const description = item.disabled ? item.disabledHint : undefined;
        const tip = description ? `${tipId}-${item.value}` : undefined;

        return (
          <button
            key={item.value}
            type="button"
            disabled={item.disabled}
            title={description}
            aria-describedby={tip}
            onClick={() => onChange(item.value)}
            className={clsx(
              "inline-flex items-center gap-1.5 rounded-full px-[13px] py-1.5 text-xs font-medium whitespace-nowrap transition-colors",
              active
                ? "text-ink bg-white font-semibold shadow-[0_1px_2px_rgba(18,53,91,.12)]"
                : "text-muted hover:text-ink",
              item.disabled && "cursor-default opacity-50 hover:text-slate-600",
            )}
          >
            {item.dot && (
              <span
                className={clsx(
                  "inline-block size-[7px] rounded-full",
                  DOT[item.dot],
                )}
              />
            )}
            {item.label}
            {item.count !== undefined && (
              <span
                className={clsx(
                  "font-bold",
                  active ? "text-indigo-600" : "text-slate-400",
                )}
              >
                {item.count}
              </span>
            )}
            {/* Read after the tab's name, never as part of it. */}
            {tip && (
              <span id={tip} hidden>
                {description}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
